/**
 * Normalize API "confidence" / avg_confidence values for display as 0–100%.
 *
 * Contract: most report list endpoints send `avg_confidence` as a **0–1 fraction**
 * (DB stores 0–100 integer, API divides by 100).
 *
 * Bugs and legacy paths produce:
 * - Fractions slightly > 1 before clamp (e.g. 1.03) → cap after scaling.
 * - Values already in **percent points** (1–100) without the /100 step → must not multiply by 100 again.
 * - Inflated fractions (e.g. 3.82 from DB 382 with a single /100) → treat as percent points when in (1, 100].
 *
 * Examples:
 * - 0.85 → 85%
 * - 82 → 82% (already percent points)
 * - 3.82 → 3.82% (was showing as 382% when multiplied by 100 again)
 */

export function confidenceValueToPercentPoints(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value) || !Number.isFinite(value)) {
    return 0;
  }
  let pct: number;
  if (value >= 0 && value <= 1) {
    pct = value * 100;
  } else if (value > 1 && value <= 100) {
    pct = value;
  } else {
    pct = value / 100;
  }
  return Math.min(100, Math.max(0, pct));
}

/** Integer percent for badges and progress bars. */
export function formatConfidencePercent(value: number | null | undefined): string {
  return `${Math.round(confidenceValueToPercentPoints(value))}%`;
}

/** Optional fractional display (e.g. 3.8%). */
export function formatConfidencePercentWithDecimals(
  value: number | null | undefined,
  fractionDigits = 1,
): string {
  return `${confidenceValueToPercentPoints(value).toFixed(fractionDigits)}%`;
}
